<template>
	<div class="head-image" @click="showUserInfo($event)" :style="{ cursor: isShowUserInfo ? 'pointer' : null }">
		<img 
			class="avatar-image" 
			v-show="displayUrl && !imageError" 
			:src="displayUrl" 
			:style="avatarImageStyle" 
			@load="onImageLoad"
			@error="onImageError"
			loading="lazy" 
		/>
		<div class="avatar-text" v-show="!displayUrl || imageError" :style="avatarTextStyle">
			{{ name?.substring(0, 2).toUpperCase() }}
		</div>
		<div class="loading-placeholder" v-show="loading" :style="loadingStyle">
			<i class="el-icon-loading"></i>
		</div>
		<div v-show="online" class="online" title="用户当前在线"></div>
		<slot></slot>
	</div>
</template>

<script>
import imageCache from '@/utils/imageCache'

export default {
	name: "headImage",
	data() {
		return {
			colors: ["#5daa31", "#c7515a", "#e03697", "#85029b",
				"#c9b455", "#326eb6"
			],
			displayUrl: '',
			loading: false,
			imageError: false,
			intersectionObserver: null
		}
	},
	props: {
		id: {
			type: Number
		},
		size: {
			type: Number,
			default: 42
		},
		width: {
			type: Number
		},
		height: {
			type: Number
		},
		radius: {
			type: String,
			default: "50%"
		},
		url: {
			type: String
		},
		name: {
			type: String,
			default: null
		},
		online: {
			type: Boolean,
			default: false
		},
		isShowUserInfo: {
			type: Boolean,
			default: true
		}
	},
	mounted() {
		this.setupIntersectionObserver()
		this.loadImage()
	},
	beforeDestroy() {
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
		}
	},
	watch: {
		url: {
			handler(newUrl) {
				this.loadImage()
			},
			immediate: false
		}
	},
	methods: {
		/**
		 * 设置交叉观察器实现懒加载
		 */
		setupIntersectionObserver() {
			if ('IntersectionObserver' in window) {
				this.intersectionObserver = new IntersectionObserver(
					(entries) => {
						entries.forEach(entry => {
							if (entry.isIntersecting) {
								this.loadImage()
								this.intersectionObserver.unobserve(entry.target)
							}
						})
					},
					{ threshold: 0.1 }
				)
				this.intersectionObserver.observe(this.$el)
			} else {
				// 不支持交叉观察器，直接加载
				this.loadImage()
			}
		},

		/**
		 * 加载图片（带缓存）
		 */
		async loadImage() {
			if (!this.url) {
				this.displayUrl = ''
				return
			}

			// 如果已经有显示的URL，不重复加载
			if (this.displayUrl === this.url) return

			this.loading = true
			this.imageError = false

			try {
				const cachedUrl = await imageCache.getImage(this.url)
				this.displayUrl = cachedUrl || this.url
			} catch (error) {
				console.warn('图片加载失败:', error)
				this.displayUrl = this.url // fallback到原URL
			} finally {
				this.loading = false
			}
		},

		/**
		 * 图片加载完成
		 */
		onImageLoad() {
			this.loading = false
			this.imageError = false
		},

		/**
		 * 图片加载错误
		 */
		onImageError() {
			this.loading = false
			this.imageError = true
			console.warn('图片加载失败:', this.url)
		},

		showUserInfo(e) {
			if (!this.isShowUserInfo) return;
			if (this.id && this.id > 0) {
				this.$http({
					url: `/user/find/${this.id}`,
					method: 'get'
				}).then((user) => {
					this.$store.commit("setUserInfoBoxPos", e);
					this.$store.commit("showUserInfoBox", user);
				})
			}
		}
	},
	computed: {
		avatarImageStyle() {
			let w = this.width ? this.width : this.size;
			let h = this.height ? this.height : this.size;
			return `width:${w}px; height:${h}px;
					border-radius: ${this.radius};`
		},
		avatarTextStyle() {
			let w = this.width ? this.width : this.size;
			let h = this.height ? this.height : this.size;
			return `
				  width: ${w}px;height:${h}px;
					background-color: ${this.name ? this.textColor : '#fff'};
					font-size:${w * 0.35}px;
					border-radius: ${this.radius};
					`
		},
		loadingStyle() {
			let w = this.width ? this.width : this.size;
			let h = this.height ? this.height : this.size;
			return `
				width: ${w}px;
				height: ${h}px;
				border-radius: ${this.radius};
			`
		},
		textColor() {
			let hash = 0;
			for (var i = 0; i < this.name.length; i++) {
				hash += this.name.charCodeAt(i);
			}
			return this.colors[hash % this.colors.length];
		}
	}
}
</script>

<style scoped lang="scss">
.head-image {
	position: relative;
	//cursor: pointer;

	.avatar-image {
		position: relative;
		overflow: hidden;
		display: block;
	}

	.avatar-text {
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
		//border: 1px solid #ccc;
		//box-shadow: var(--im-box-shadow);
	}

	.loading-placeholder {
		position: absolute;
		top: 0;
		left: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: #f5f7fa;
		color: #c0c4cc;
		font-size: 14px;

		.el-icon-loading {
			animation: rotating 2s linear infinite;
		}
	}

	.online {
		position: absolute;
		right: -5px;
		bottom: 0;
		width: 12px;
		height: 12px;
		background: limegreen;
		border-radius: 50%;
		border: 2px solid white;
	}
}

@keyframes rotating {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}
</style>